// The schedule's side column (B-386): one team's season so far, its road to
// the playoffs, and at Everything how hard its schedule is against the
// league's. Facts only: records, scores and ranks, never what to do.

import type { BracketView, ScheduleView, TeamRef } from '@fsp/domain';
import { winShare } from '@fsp/domain';
import { Detail, useAtLeast } from '../../data/detail';
import { TeamMark } from '../../kit';
import { Panel } from '../../kit/Panel';
import { Button } from '../../kit/ui';
import { Term } from '../../kit/Why';
import { ordinal } from '../shared/RosterMoveSheet';
import { pointsOf, recordText, streakOf, weekKey, type Week } from './season';

/** The record, the run it is on, the season week by week, and the points. */
export function SeasonSoFar({
  team,
  weeks,
  rank,
  of,
}: {
  team: TeamRef;
  weeks: readonly Week[];
  /** The team's place in the table: mine is served; another's is not on the schedule. */
  rank: number | null;
  of: number;
}) {
  const standard = useAtLeast('standard');
  const streak = streakOf(weeks);
  const points = pointsOf(weeks);
  // Every week the team plays or sits out, and the playoff weeks ahead.
  const shown = weeks.filter(
    (w) => w.game !== null || w.bye || w.p.myField !== null || w.p.kind !== 'regular',
  );
  return (
    <Panel title="Season so far">
      <div className="fsp-sc-sofar">
        <div className="fsp-sc-record">
          <TeamMark team={team} size={28} />
          <span className="fsp-sc-record-who">
            <span className="fsp-sc-record-name">{team.name}</span>
            <span className="fsp-sc-record-line">
              <span className="fsp-num fsp-sc-record-wl">{recordText(team.record)}</span>
              {rank !== null ? (
                <span className="fsp-muted">
                  {' '}
                  · {ordinal(rank)} of {of}
                </span>
              ) : null}
            </span>
          </span>
        </div>
        {/* THE SEASON IN A ROW OF SQUARES: each week's result in a letter
            and a color (UX-28), the week under way ringed, the weeks to come
            empty, the playoffs after a gap. Marks, not controls: a square
            is too small for a finger's 44 (UX-6) in a row of them, and the
            season below says each game; one sentence reads them out. */}
        <div
          className="fsp-sc-strip"
          role="img"
          aria-label={shown.map((w) => squareTip(w, standard)).join(' ')}
        >
          {shown.map((w) => (
            <span
              key={weekKey(w)}
              className="fsp-sc-square"
              data-outcome={w.outcome ?? (w.bye ? 'bye' : undefined)}
              data-current={w.p.period.isCurrent || undefined}
              data-kind={w.p.kind === 'regular' ? undefined : 'playoff'}
            >
              {w.outcome === 'win'
                ? 'W'
                : w.outcome === 'loss'
                  ? 'L'
                  : w.outcome === 'tie'
                    ? 'T'
                    : w.p.period.index}
            </span>
          ))}
        </div>
        {/* The run and the points are numbers: from Standard. */}
        <Detail min="standard">
          <div className="fsp-sc-facts">
            {streak !== null ? (
              <div>
                {streak.outcome === 'win' ? 'Won' : streak.outcome === 'loss' ? 'Lost' : 'Tied'}{' '}
                {streak.count === 1 ? 'the last game' : `${streak.count} in a row`}
              </div>
            ) : null}
            {points.games > 0 ? (
              <div>
                Scored <span className="fsp-num">{points.scored.toFixed(1)}</span>, allowed{' '}
                <span className="fsp-num">{points.allowed.toFixed(1)}</span>
                <span className="fsp-muted">
                  {' '}
                  · <span className="fsp-num">{(points.scored / points.games).toFixed(1)}</span> a
                  week
                </span>
              </div>
            ) : null}
          </div>
        </Detail>
      </div>
    </Panel>
  );
}

/** A week in words, as a square says it; its score from Standard, as the rows do. */
function squareTip(w: Week, standard: boolean): string {
  const label = w.second ? `${w.p.period.label}, second game` : w.p.period.label;
  if (w.rest) return `${label}: sits out this game.`;
  if (w.bye) return `${label}: a bye week.`;
  if (w.p.myField !== null && w.game === null) return `${label}: against the whole league.`;
  if (w.game === null && w.p.kind !== 'regular')
    return `${label}: playoffs${w.p.matchups.length ? '' : ', the bracket to be decided'}.`;
  if (w.game === null || w.us === null || w.them === null) return `${label}: no game.`;
  const where = `${w.home ? 'vs' : 'at'} ${w.them.team.name}`;
  const score = standard ? ` ${w.us.score.toFixed(1)}–${w.them.score.toFixed(1)}` : '';
  switch (w.outcome) {
    case 'win':
      return `${label}: won${score} ${where}.`;
    case 'loss':
      return `${label}: lost${score} ${where}.`;
    case 'tie':
      return `${label}: tied${score} ${where}.`;
    case 'live':
      return `${label}:${score} ${where}, in progress.`;
    default:
      return `${label}: ${where}.`;
  }
}

/** How many make it, how long the regular season has to run, and where the team would be. */
export function RoadToPlayoffs({
  view,
  team,
  bracket,
  onPicture,
}: {
  view: ScheduleView;
  team: TeamRef;
  bracket: BracketView | null;
  onPicture: () => void;
}) {
  const standard = useAtLeast('standard');
  const mine = team.id === view.myTeamId;
  const left = view.periods.filter(
    (p) =>
      p.kind === 'regular' && p.matchups.length > 0 && p.matchups.some((m) => m.status !== 'final'),
  ).length;
  const rounds =
    bracket !== null && bracket.rounds.length > 0
      ? bracket.rounds.map((r) => ({ name: r.name, label: r.period.label }))
      : view.periods
          .filter((p) => p.kind === 'playoff')
          .map((p) => ({ name: 'Playoffs', label: p.period.label }));
  const seed = bracket?.seeds.find((s) => s.team.id === team.id) ?? null;
  const picture =
    bracket === null || bracket.status !== 'projected' || bracket.throughPeriod === 0
      ? null
      : seed !== null
        ? `If the season ended today, ${mine ? 'you would be' : 'they would be'} the ${seed.seed} seed.`
        : `If the season ended today, ${mine ? 'you would' : 'they would'} miss the playoffs.`;
  if (view.playoffTeamCount === 0) {
    return (
      <Panel title="Road to the title">
        <div className="fsp-sc-road">
          <p>No playoffs in this league: the table at the end of the season decides it.</p>
        </div>
      </Panel>
    );
  }
  return (
    <Panel title="Road to the playoffs">
      <div className="fsp-sc-road">
        <p>
          {view.playoffTeamCount} of {view.record.of} teams make the playoffs.
        </p>
        <p>
          The regular season ends with Week {view.regularSeasonEnd}
          {standard && left > 0 ? `, ${left} week${left === 1 ? '' : 's'} to play` : ''}.
        </p>
        {rounds.length > 0 ? (
          <ul className="fsp-sc-rounds">
            {rounds.map((r) => (
              <li key={`${r.name}-${r.label}`}>
                <span className="fsp-sc-round-name">{r.name}</span>
                <span className="fsp-muted">{r.label}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {/* CLINCHED, IN WORDS, at every level (B-397): certain, from the
            server, where the seed beside it is what the table says today. */}
        {seed?.status === 'clinched' ? (
          <p className="fsp-sc-picture">
            {mine ? 'You have' : 'They have'} clinched a playoff spot.
          </p>
        ) : null}
        {/* Where the team would be is a seed, a number: from Standard. */}
        {picture !== null && standard ? <p className="fsp-sc-picture">{picture}</p> : null}
        <span>
          <Button size="xs" variant="quiet" icon="podium" onClick={onPicture}>
            Playoff picture
          </Button>
        </span>
      </div>
    </Panel>
  );
}

/**
 * How hard a team's schedule is, played and still to play, and every team's
 * still to play in order (B-386): a number, so at Everything (DETAIL_LEVELS
 * §Schedule). A team's row shows its season.
 */
export function StrengthOfSchedule({
  view,
  teamId,
  onPick,
}: {
  view: ScheduleView;
  teamId: string;
  onPick: (teamId: string) => void;
}) {
  const pro = useAtLeast('pro');
  if (!pro || view.strength.length === 0) return null;
  const teams = new Map<string, TeamRef>();
  for (const p of view.periods)
    for (const m of p.matchups) {
      teams.set(m.home.team.id, m.home.team);
      teams.set(m.away.team.id, m.away.team);
    }
  const own = view.strength.find((s) => s.teamId === teamId) ?? null;
  const ranked = [...view.strength]
    .filter((s) => s.remainingRank !== null)
    .sort((a, b) => a.remainingRank! - b.remainingRank!);
  const ranks = ranked.length;
  return (
    <Panel
      title="Strength of schedule"
      meta={
        <span className="fsp-muted fsp-sb-count">
          <Term tip="How good a team’s opponents are: their records now, added up over each game, and their average score a week. The hardest still to play is first: by the records, then by the points.">
            What this is
          </Term>
        </span>
      }
    >
      {own !== null ? (
        <div className="fsp-sos-own">
          <Part label="Played" part={own.played} />
          <Part label="Still to play" part={own.remaining} />
          {own.remainingRank !== null ? (
            <p className="fsp-sos-rank">
              {ordinal(own.remainingRank)} hardest still to play, of {ranks}.
            </p>
          ) : null}
        </div>
      ) : null}
      {ranked.length > 0 ? (
        <ol className="fsp-sos-list" aria-label="Every team, hardest schedule still to play first">
          {ranked.map((s) => {
            const t = teams.get(s.teamId);
            if (t === undefined) return null;
            const share = winShare(s.remaining.opponents);
            return (
              <li key={s.teamId}>
                {/* Named by what it shows, and what it does after: a name
                    that left out the words on it was a different button to
                    a screen reader (axe's label-content-name-mismatch). */}
                <button
                  type="button"
                  className="fsp-sos-row"
                  aria-current={s.teamId === teamId || undefined}
                  onClick={() => onPick(s.teamId)}
                >
                  <span className="fsp-sos-place fsp-num">{s.remainingRank}</span>
                  <TeamMark team={t} size={18} />
                  <span className="fsp-sos-name">{t.name}</span>
                  <span className="fsp-sos-rec fsp-num">
                    {recordText(s.remaining.opponents)}
                    {share !== null ? (
                      <span className="fsp-muted"> {share.toFixed(3).replace(/^0/, '')}</span>
                    ) : null}
                  </span>
                  <span className="fsp-sr">: show its season</span>
                </button>
              </li>
            );
          })}
        </ol>
      ) : null}
    </Panel>
  );
}

function Part({
  label,
  part,
}: {
  label: string;
  part: ScheduleView['strength'][number]['played'];
}) {
  const share = winShare(part.opponents);
  return (
    <div className="fsp-sos-part">
      <span className="fsp-sos-part-label">
        {label}
        <span className="fsp-muted">
          {' '}
          · {part.games} game{part.games === 1 ? '' : 's'}
        </span>
      </span>
      <span className="fsp-sos-part-line">
        {part.games === 0 ? (
          <span className="fsp-muted">None</span>
        ) : (
          <>
            Opponents <span className="fsp-num">{recordText(part.opponents)}</span>
            {share !== null ? (
              <span className="fsp-muted fsp-num"> ({share.toFixed(3).replace(/^0/, '')})</span>
            ) : null}
            {part.pointsPerGame !== null ? (
              <span className="fsp-muted">
                {' '}
                · <span className="fsp-num">{part.pointsPerGame.toFixed(1)}</span> a week
              </span>
            ) : null}
          </>
        )}
      </span>
    </div>
  );
}
